import React, { useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Snackbar,
  TextField,
} from '@mui/material';
import { DataGrid, type GridColDef, type GridPaginationModel } from '@mui/x-data-grid';
import { useReferralList, useRevokeReferral } from '../hooks/useReferrals';
import type { ReferralRow, ReferralStatus } from '../types';

const COLORS: Record<ReferralStatus, 'default' | 'success' | 'error'> = {
  REGISTERED: 'default',
  QUALIFIED: 'success',
  REVOKED: 'error',
};

const fmt = (value?: string | null) => (value ? new Date(value).toLocaleString() : '-');

export const ReferralListPage: React.FC = () => {
  const [status, setStatus] = useState<ReferralStatus | ''>('');
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [target, setTarget] = useState<ReferralRow | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const { data, isLoading } = useReferralList({
    status,
    search: search.trim(),
    page: pagination.page + 1,
    pageSize: pagination.pageSize,
  });
  const revoke = useRevokeReferral();

  const confirmRevoke = async () => {
    if (!target) return;
    try {
      const result = await revoke.mutateAsync(target.publicId);
      setToast(`Referral revoked. ${result.revokedCoupons} unused reward code(s) deactivated.`);
    } catch {
      setToast('Could not revoke the referral.');
    } finally {
      setTarget(null);
    }
  };

  const columns: GridColDef<ReferralRow>[] = [
    {
      field: 'referrer',
      headerName: 'Referrer',
      flex: 1,
      minWidth: 220,
      valueGetter: (_v, row) => row.referrer.email,
    },
    {
      field: 'referee',
      headerName: 'Friend',
      flex: 1,
      minWidth: 220,
      valueGetter: (_v, row) => row.referee.email,
    },
    { field: 'referralCode', headerName: 'Code', width: 130 },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (p) => <Chip size="small" label={p.row.status} color={COLORS[p.row.status]} />,
    },
    {
      field: 'createdAt',
      headerName: 'Joined',
      width: 180,
      valueGetter: (_v, row) => fmt(row.createdAt),
    },
    {
      field: 'qualifiedAt',
      headerName: 'First payment',
      width: 180,
      valueGetter: (_v, row) => fmt(row.qualifiedAt),
    },
    {
      field: 'actions',
      headerName: '',
      width: 110,
      sortable: false,
      renderCell: (p) =>
        p.row.status === 'REVOKED' ? null : (
          <Button size="small" color="error" onClick={() => setTarget(p.row)}>
            Revoke
          </Button>
        ),
    },
  ];

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div>
        <h1 className="text-2xl font-semibold">Referrals</h1>
        <p className="text-sm text-gray-500">
          Friends who joined through a referral code. Rewards come from campaigns with a referral
          trigger.
        </p>
      </div>
      <div className="flex flex-wrap gap-3">
        <TextField
          size="small"
          label="Search email or code"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPagination((p) => ({ ...p, page: 0 }));
          }}
          sx={{ minWidth: 260 }}
        />
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as ReferralStatus | '');
            setPagination((p) => ({ ...p, page: 0 }));
          }}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="REGISTERED">Registered</MenuItem>
          <MenuItem value="QUALIFIED">Qualified</MenuItem>
          <MenuItem value="REVOKED">Revoked</MenuItem>
        </TextField>
      </div>
      <DataGrid
        autoHeight
        rows={data?.items ?? []}
        getRowId={(row) => row.publicId}
        columns={columns}
        loading={isLoading}
        rowCount={data?.total ?? 0}
        paginationMode="server"
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        pageSizeOptions={[25, 50, 100]}
        disableRowSelectionOnClick
        localeText={{ noRowsLabel: 'No referrals yet.' }}
      />

      <Dialog open={Boolean(target)} onClose={revoke.isPending ? undefined : () => setTarget(null)}>
        <DialogTitle>Revoke this referral?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" className="mt-1">
            {target?.referee.email} will no longer count as referred by {target?.referrer.email}.
            Unused reward codes issued for this referral are deactivated. This cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setTarget(null)} disabled={revoke.isPending}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void confirmRevoke()}
            disabled={revoke.isPending}
          >
            Revoke
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        message={toast ?? ''}
      />
    </div>
  );
};
